import React, { useEffect, useState } from 'react'
import {
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonToolbar,
  IonToast,
  IonLoading,
} from '@ionic/react'
import { trash, ellipsisHorizontal, ellipsisVertical, chevronForwardOutline } from 'ionicons/icons'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { setIsPCROpening, upgradePCR } from '../store/home.actions'
import { deletePCR } from '../../home/store/home.actions'
import { getUserSelector } from '../../user/selectors'
import { getPCRDocsSelector } from '../selectors'
import FilterComponent from '../filter/filter.component'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { getHomeSelector } from '../selectors/getHome.selector'
import './Home.page.css'
import { getFilteredPCRDocsSelector } from '../selectors/getFilteredPCRDocs.selector'
import Header from '../../../components/header/Header.component'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import HomeHeader from '../homeHeader/HomeHeader.component'
import { useTranslation } from 'react-i18next'
import LabelComponent from '../../../components/label/Label.component'
import Logger from '../../../services/logger.service'
import { AuthenticationService } from '../../login/services/authentication.service'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { truncateSecondsIfNecessary } from '../../../utilities/dateTime.util'
import { IServiceConfig } from '../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { IPcrSummary, IPcrSummaries } from '../store/home.types'
import LockInChartChips from '../../completeChart/component/LockInChartChips.component'
import { useOfflineStatus } from '../../../utilities/isOnline.util'
import { preSubmitChart } from '../../submitChart/store/submitChart.actions'
import { ToastColor } from '../../../components/toast/store/toast.constants'
import { getToastProps } from '../../../components/toast/toastIcon.util'

const Home: React.FC = (props: any) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const user = useSelector(getUserSelector)
  const { userId, serviceId, serviceName } = user

  const pcrDocs: IPcrSummaries = useSelector(getPCRDocsSelector)
  const filteredPCRDocs = useSelector(getFilteredPCRDocsSelector)
  const { hideComponent, isPCROpening, isPCRClosing } = useSelector(getHomeSelector)

  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS
  const isOfflineStatus = useOfflineStatus()

  const allowSecureRoutes = AuthenticationService.allowSecureRoutes(user)
  useEffect(() => {
    if (!allowSecureRoutes) {
      Logger.warn('User is not authenticated. Redirecting to index route.')
      history.push(AppRouterConstants.INDEX_ROUTE)
    }
  }, [allowSecureRoutes])

  const successToast = (props.location.state && props.location.state.showToast) || false

  const handleDelete = (pcrId: string) => {
    dispatch(deletePCR(pcrId))
  }

  const onDeleteClick = (item: IPcrSummary) => {
    const alertState = {
      header: 'Delete PCR Record',
      message: 'Are you sure you want to delete this record?',
      buttons: [{ text: 'Cancel' }, { text: 'Ok', role: 'destructive', handler: () => handleDelete(item.pcrId) }],
    }
    dispatch(showAlert(alertState))
  }

  const onMoreClick = () => {
    window.alert('Not Implemented')
  }

  const [showToast, setShowToast] = useState(true)

  const isLoading = isPCROpening || isPCRClosing
  const loadingMessage = isPCROpening ? t('home.openingChart') : t('home.closingChart')

  return (
    <IonPage>
      <Header title={serviceName} />
      <HomeHeader />
      {successToast && (
        <IonToast
          isOpen={showToast}
          position='top'
          message={t('home.ShiftSetupComplete')}
          {...getToastProps(ToastColor.Success)}
          duration={2000}
          onDidDismiss={() => setShowToast(false)}
        />
      )}
      <IonContent fullscreen className='bg-lt-grey no-footer-content' color='light'>
        {pcrDocs && hideComponent && <FilterComponent />}
        {pcrDocs && (
          <div>
            <IonToolbar color='light' className='side-padding hide-md-down home-toolbar'>
              <IonRow className='right-margin'>
                <IonCol sizeMd='2'>
                  <IonLabel>
                    <h4>Time</h4>
                  </IonLabel>
                </IonCol>
                <IonCol sizeMd='2'>
                  <IonLabel>
                    <h4>Pick-up Location</h4>
                  </IonLabel>
                </IonCol>
                <IonCol sizeMd='2'>
                  <IonLabel>
                    <h4>Call Nature</h4>
                  </IonLabel>
                </IonCol>
                <IonCol sizeMd='2'>
                  <IonLabel>
                    <h4>Patient Name</h4>
                  </IonLabel>
                </IonCol>
                <IonCol sizeMd='1'>
                  <IonLabel>
                    <h4>Unit</h4>
                  </IonLabel>
                </IonCol>
                <IonCol>
                  <IonLabel>
                    <h4>Status</h4>
                  </IonLabel>
                </IonCol>
                <IonLabel color='medium' className='swipe-text'>
                  Swipe for Actions
                </IonLabel>
              </IonRow>
            </IonToolbar>
            <IonList>
              {filteredPCRDocs.map((item: IPcrSummary) => (
                <IonItemSliding key={item.pcrId}>
                  <IonItem
                    button
                    detail={false}
                    data-testid={item.pcrId}
                    key={item.pcrId}
                    onClick={() => {
                      if (item.isChartLockedIn === 1 && !isOfflineStatus) {
                        dispatch(setIsPCROpening(true))
                        dispatch(upgradePCR(item))
                        dispatch(preSubmitChart())
                        history.push(TabConstants.SHARE_TAB)
                        Logger.info(`Locked PCR: ${item.pcrId} uploaded by user: ${userId} in service: ${serviceId}`)
                      } else if (item.isChartLockedIn === 1 && isOfflineStatus) {
                        const alertState = {
                          header: 'Error',
                          message: 'This chart is locked. Please upload it once you are online.',
                          buttons: [{ text: 'Ok' }],
                        }
                        dispatch(showAlert(alertState))
                        return
                      } else if (isLoading) return
                      else {
                        dispatch(setIsPCROpening(true))
                        dispatch(upgradePCR(item))
                        history.push(TabConstants.CHART_ROUTE)
                        Logger.info(`PCR: ${item.pcrId} opened by user: ${userId} in service: ${serviceId}`)
                      }
                    }}>
                    <IonGrid class='home-grid no-padding hide-md-up'>
                      <IonRow>
                        <IonCol className='ion-flex' sizeXs='6'>
                          <IonLabel>
                            <h2>{truncateSecondsIfNecessary(item.time, displaySeconds)}</h2>
                          </IonLabel>
                          <IonLabel className='padding-l-xs' color='medium'>
                            <h3>{item.date}</h3>
                          </IonLabel>
                        </IonCol>
                        <IonCol>
                          <IonLabel>
                            <h3>{item.pickupLocation}</h3>
                          </IonLabel>
                        </IonCol>
                        <IonCol>
                          <IonLabel>
                            <h3>{item.callNature}</h3>
                          </IonLabel>
                        </IonCol>
                      </IonRow>
                      <IonRow>
                        <IonCol>
                          <IonLabel>
                            <h3>{item.unit}</h3>
                          </IonLabel>
                        </IonCol>
                        <IonCol>
                          <LabelComponent isPHI label={item.patientName} size={3} />
                        </IonCol>
                      </IonRow>
                      <IonRow>
                        <IonCol>
                          <LockInChartChips isChartLockedIn={item.isChartLockedIn} chartStatus={item.status} />
                        </IonCol>
                      </IonRow>
                    </IonGrid>
                    <IonGrid class='home-grid no-padding hide-md-down'>
                      <IonRow>
                        <IonCol sizeMd='2'>
                          <IonLabel>
                            <h3>{item.time}</h3>
                          </IonLabel>
                          <IonLabel color='medium'>
                            <h4>{item.date}</h4>
                          </IonLabel>
                        </IonCol>
                        <IonCol sizeMd='2'>
                          <LabelComponent isPHI label={item.pickupLocation} size={3} />
                        </IonCol>
                        <IonCol sizeMd='2'>
                          <IonLabel>
                            <h3>{item.callNature}</h3>
                          </IonLabel>
                        </IonCol>
                        <IonCol sizeMd='2'>
                          <LabelComponent isPHI label={item.patientName} size={3} />
                        </IonCol>
                        <IonCol sizeMd='1'>
                          <IonLabel>
                            <h3>{item.unit}</h3>
                          </IonLabel>
                        </IonCol>
                        <IonCol sizeMd='2'>
                          <LockInChartChips isChartLockedIn={item.isChartLockedIn} chartStatus={item.status} />
                        </IonCol>
                      </IonRow>
                    </IonGrid>
                    <IonIcon color='medium' icon={chevronForwardOutline} />
                  </IonItem>
                  <IonItemOptions>
                    {hideComponent && (
                      <IonItemOption color='primary' onClick={() => onMoreClick()}>
                        <IonIcon slot='start' ios={ellipsisHorizontal} md={ellipsisVertical} />
                        More
                      </IonItemOption>
                    )}
                    <IonItemOption color='danger' onClick={(e) => onDeleteClick(item)}>
                      <IonIcon slot='start' icon={trash} />
                      Delete
                    </IonItemOption>
                  </IonItemOptions>
                </IonItemSliding>
              ))}
            </IonList>
          </div>
        )}
        {isLoading && (
          <IonLoading data-testid='Loader' isOpen animated showBackdrop spinner={'crescent'} message={loadingMessage} />
        )}
      </IonContent>
    </IonPage>
  )
}

export default Home
